/**
 * Centralized instrumentation for Grafana Faro
 * Provides domain-specific tracking methods with graceful degradation
 */

/**
 * Instrumentation class wrapping Faro API
 */
export class Instrumentation {
    /**
     * Initialize with Faro instance
     * @param {Object} faroInstance - Faro SDK instance (can be null)
     */
    constructor(faroInstance) {
        this.faro = faroInstance;
    }

    /**
     * Track a custom event
     * @param {string} name - Event name
     * @param {Object} attributes - Event attributes
     */
    trackEvent(name, attributes = {}) {
        try {
            if (this.faro?.api?.pushEvent) {
                this.faro.api.pushEvent(name, attributes);
            }

            // Development mode logging
            if (window.DEBUG_INSTRUMENTATION) {
                console.log('[Instrumentation Event]', name, attributes);
            }
        } catch (error) {
            // Silently fail - instrumentation should never break the app
            console.warn('Instrumentation trackEvent failed:', error);
        }
    }

    /**
     * Track an error
     * @param {Error} error - Error object
     * @param {Object} context - Additional context
     */
    trackError(error, context = {}) {
        try {
            if (this.faro?.api?.pushError) {
                this.faro.api.pushError(error, context);
            }

            // Development mode logging
            if (window.DEBUG_INSTRUMENTATION) {
                console.error('[Instrumentation Error]', error, context);
            }
        } catch (err) {
            // Silently fail - instrumentation should never break the app
            console.warn('Instrumentation trackError failed:', err);
        }
    }

    /**
     * Start an OpenTelemetry span for timing operations
     * Note: Requires Faro's TracingInstrumentation to provide OTel API
     * @param {string} name - Span name
     * @param {Object} attributes - Span attributes
     * @returns {Object|null} Span object or null if unavailable
     */
    startSpan(name, attributes = {}) {
        try {
            // Access OpenTelemetry tracer via global API (provided by Faro TracingInstrumentation)
            if (window.opentelemetry?.trace) {
                const tracer = window.opentelemetry.trace.getTracer('taxonomic');
                const span = tracer.startSpan(name, { attributes });

                if (window.DEBUG_INSTRUMENTATION) {
                    console.log('[Instrumentation Span Start]', name, attributes);
                }

                return span;
            }
            return null;
        } catch (error) {
            console.warn('Instrumentation startSpan failed:', error);
            return null;
        }
    }

    /**
     * End an OpenTelemetry span
     * @param {Object} span - Span to end (can be null)
     */
    endSpan(span) {
        try {
            if (span?.end) {
                span.end();

                if (window.DEBUG_INSTRUMENTATION) {
                    console.log('[Instrumentation Span End]');
                }
            }
        } catch (error) {
            console.warn('Instrumentation endSpan failed:', error);
        }
    }

    /**
     * Track puzzle loaded event
     * @param {string} date - Puzzle date (YYYY-MM-DD)
     * @param {number} categoryCount - Number of categories
     */
    trackPuzzleLoaded(date, categoryCount) {
        this.trackEvent('puzzle_loaded', {
            puzzleDate: date,
            categoryCount: categoryCount
        });
    }

    /**
     * Track category completed event
     * @param {string} categoryId - Category ID
     * @param {string} categoryName - Category display name
     * @param {number} completedCount - Number of categories completed so far
     * @param {number} totalCategories - Total categories in puzzle
     */
    trackCategoryCompleted(categoryId, categoryName, completedCount, totalCategories) {
        this.trackEvent('category_completed', {
            categoryId: categoryId,
            categoryName: categoryName,
            completedCount: completedCount,
            totalCategories: totalCategories
        });
    }

    /**
     * Track deep dive entered event
     * @param {string} categoryId - Category ID
     * @param {string} categoryName - Category display name
     * @param {string} miniGameType - Mini-game type/mechanic
     */
    trackDeepDiveEntered(categoryId, categoryName, miniGameType) {
        this.trackEvent('deep_dive_entered', {
            categoryId: categoryId,
            categoryName: categoryName,
            miniGameType: miniGameType
        });
    }

    /**
     * Track deep dive exited event
     * @param {string} categoryId - Category ID
     * @param {boolean} success - Whether mini-game was completed successfully
     */
    trackDeepDiveExited(categoryId, success) {
        this.trackEvent('deep_dive_exited', {
            categoryId: categoryId,
            success: success
        });
    }

    /**
     * Track game won event
     * @param {string} date - Puzzle date (YYYY-MM-DD)
     * @param {number} totalCategories - Number of categories completed
     */
    trackGameWon(date, totalCategories) {
        this.trackEvent('game_won', {
            puzzleDate: date,
            totalCategories: totalCategories
        });
    }
}

/**
 * Singleton instance (initialized in app.js after Faro init)
 */
export let instrumentation = null;

/**
 * Initialize the instrumentation singleton
 * @param {Object} faroInstance - Faro SDK instance
 */
export function initInstrumentation(faroInstance) {
    instrumentation = new Instrumentation(faroInstance);
}
